import { ReactNode } from 'react';
import styles from './style.module.scss';

/**
 * Spacing between the sections of a profile tab.
 * `none` keeps the sections flush, as the plain-text tabs do.
 */
type SectionsGap = 'none' | 'sm' | 'md';

/**
 * `rows`         – stacked label/value pairs (default).
 * `stack`        – stacked free-form content, flush with the section edge.
 * `inline`       – side-by-side content, indented like the row layout.
 * `inlineFlush`  – side-by-side content, flush with the section edge.
 */
type BodyLayout = 'rows' | 'stack' | 'inline' | 'inlineFlush';

export function ProfileSections({ children, gap = 'none' }: { children: ReactNode; gap?: SectionsGap }) {
    return <div className={styles.sections} data-gap={gap}>{children}</div>;
};

interface ProfileSectionProps {
    title: string;
    /** Rendered at the far end of the head bar, e.g. a download link. */
    action?: ReactNode;
    layout?: BodyLayout;
    children: ReactNode;
};

export function ProfileSection(props: ProfileSectionProps) {
    const { title, action, layout = 'rows', children } = props;

    return (
        <div className={styles.section}>
            <div className={styles.section_headbar}>
                <span className={styles.section_headbar_title}>{title}</span>
                {action}
            </div>
            <div className={styles.section_body} data-layout={layout}>
                {children}
            </div>
        </div>
    );
};

interface ProfileRowProps {
    label: string;
    children: ReactNode;
    /** Slightly wider label column, used by the education tab. */
    wide?: boolean;
};

export function ProfileRow({ label, children, wide }: ProfileRowProps) {
    return (
        <div className={styles.row}>
            <span className={styles.row_prop} data-wide={wide === true}>{label}</span>
            <span className={styles.row_val}>{children}</span>
        </div>
    );
};
